import axios from 'axios';
import { dispatchAdminOrchestratorApiError } from '../lib/adminApiEvents';
import { shouldSuppressAdminOrchestratorGlobalError } from '../lib/adminApiErrorPolicy';
import { userMessageForAdminHttp } from '../lib/axiosErrorMessage';
import { reportClientError } from '../lib/telemetry';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, Layout global snackbar is skipped (caller shows its own Alert). */
    suppressGlobalErrorNotification?: boolean;
  }
}

/**
 * Orchestrator API origin for JSON calls (always direct to orchestrator — no Vite proxy).
 * - `VITE_ORCHESTRATOR_API_BASE=http://localhost:3000` (orchestrator origin, no path)
 * - Dev default when unset: `http://localhost:3000`
 * - Prod default when unset: same-origin `/api/orchestrator/v1` (ingress routes to orchestrator)
 */
function orchestratorApiRoot(): string {
  const raw = import.meta.env.VITE_ORCHESTRATOR_API_BASE;
  const base =
    typeof raw === 'string' ? raw.trim().replace(/\/$/, '') : '';
  if (!base) {
    if (import.meta.env.DEV) {
      return 'http://localhost:3000/api/orchestrator/v1';
    }
    return '/api/orchestrator/v1';
  }
  return `${base}/api/orchestrator/v1`;
}

/** Shared axios instance for orchestrator-backed admin API calls. */
export const adminClient = axios.create({
  baseURL: orchestratorApiRoot(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Orchestrator-Session-Profile': 'operator',
  },
});

adminClient.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const config = err?.config;

    if (
      typeof status === 'number' &&
      (status === 401 || status === 403 || status === 429 || status >= 500)
    ) {
      const message = userMessageForAdminHttp(status, err?.response?.data);
      reportClientError(
        'adminClient.response',
        new Error(message),
        { status, path: config?.url },
      );

      const suppress = shouldSuppressAdminOrchestratorGlobalError({
        suppressGlobalErrorNotification: config?.suppressGlobalErrorNotification === true,
        url: config?.url,
      });
      if (!suppress) {
        dispatchAdminOrchestratorApiError({ status, message });
      }
    }

    return Promise.reject(err);
  },
);
